import React, { useState, useContext, useRef } from "react";
import QRCode from "react-qr-code";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { inpS, crd } from "../../constants/styles";
import Ic from "../common/Ic";
import CopyBtn from "../common/CopyBtn";
import { buildUpiUrl, isValidUpiId } from "../../utils/deepLinks";
import { getBankRows } from "../../utils/bankDetails";

// Tenant "Pay Now" page.
// Manual payment flow only: UPI QR, Cash, or Bank transfer.
// All payments are recorded as `pending` and require landlord approval
// before becoming "paid" (mirrors the inline panel in TenDash). Razorpay is
// intentionally NOT used for rent — it routes to the single platform merchant
// account and cannot split to individual landlords without Razorpay Route.
function TenPayNow() {
  const { t, h, pays, setPays, ccs, mts, tens, go, flash, currUserId, addNotif, settings } = useContext(Ctx);
  const ct = currUserId;
  // Maintenance charges live in their own "🔧"-prefixed payment rows. Aggregate
  // rent + all maintenance dues so this page mirrors what TenDash's "Pay Now"
  // promised. `rentRow` keeps the existing single-row code paths working
  // (myCC lookup, txn.mo, etc.); `maintRows` holds the side charges.
  const allDues = pays.filter(x => x.tid === ct && (x.st === "due" || x.st === "partial"));
  const rentRow = allDues.find(x => !(x.mo || "").startsWith("🔧")) || null;
  const maintRows = allDues.filter(x => (x.mo || "").startsWith("🔧"));
  const dp = rentRow || maintRows[0] || null;
  const me = tens.find(x => x.id === ct);
  const [showCommonSplit, setShowCommonSplit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [payMth, setPayMth] = useState("upi"); // upi | cash | bank
  const [upiOpened, setUpiOpened] = useState(false);
  const payLockRef = useRef(false); // Instant lock to prevent double-clicks
  const rentAmt = rentRow ? (rentRow.amt || 0) : 0;
  const rentCom = rentRow ? (rentRow.com || 0) : 0;
  const maintTotal = maintRows.reduce((s, m) => s + (m.amt || 0) + (m.com || 0), 0);
  const commonAndMaint = rentCom + maintTotal;
  const grossTotal = rentAmt + commonAndMaint;
  // Already-paid only counts verified txns on the rent row (maint rows are
  // typically zero-amt with no prior partials).
  const alreadyPaid = rentRow ? (rentRow.txns || []).reduce((s, tx) => s + (tx.status === "verified" ? tx.amt : 0), 0) : 0;
  const tot = Math.max(0, grossTotal - alreadyPaid);
  const [payType, setPayType] = useState("full"); // full | partial | advance
  const [payAmt, setPayAmt] = useState(0);

  // Find CC record matching the due payment month
  const myCC = dp ? (ccs.find(c => c.mo === dp.mo) || ccs[0] || null) : (ccs[0] || null);
  const myMaintCharges = mts.filter(m => m.exp_amt > 0 && m.exp_applied && m.exp_target === ct);
  const myShare = myCC && myCC.n > 0 ? Math.round(
    ((myCC.items && myCC.items.length > 0)
      ? myCC.items.reduce((s, it) => s + Number(it.amount || 0), 0)
      : (myCC.eb + myCC.cl)) / myCC.n
  ) : 0;

  // Set default pay amount to total on first render
  React.useEffect(() => { if (tot > 0 && payAmt === 0) setPayAmt(tot); }, [tot]);
  // Default advance amount when no due payment
  React.useEffect(() => { if (!dp && payAmt === 0) setPayAmt(1000); }, [dp]);

  // ── Shared payment-method picker ──────────────────────────────────
  // Renders UPI QR / Cash / Bank details based on selected `payMth`.
  // `amount` is the numeric amount to encode into the UPI deep link.
  // `onConfirmNonUpi` is fired for cash/bank methods when the tenant taps Submit.
  // For UPI, the tenant first opens their UPI app (deep link), then confirms
  // with an "I've paid" button which calls `onConfirmUpi`.
  const renderMethodPanel = ({ amount, onConfirmUpi, onConfirmNonUpi, confirmDisabled }) => (
    <>
      <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>{t.method || "Method"}</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {["upi", "cash", "bank"].map(m => (
          <button key={m} onClick={() => { setPayMth(m); setUpiOpened(false); }} style={{
            flex: 1, padding: 10, borderRadius: 10,
            border: `1px solid ${payMth === m ? h.ac : h.bd}`,
            background: payMth === m ? `${h.ac}15` : "transparent",
            color: payMth === m ? h.ac : h.sc, cursor: "pointer",
            fontSize: 12, fontWeight: 500
          }}>{m === "upi" ? (t.upi || "UPI") : m === "cash" ? (t.cash || "Cash") : (t.bank || "Bank")}</button>
        ))}
      </div>

      {payMth === "upi" && <div style={{ background: h.cd, borderRadius: 12, padding: 16, textAlign: "center", marginBottom: 12, border: `1px solid ${h.bd}` }}>
        {isValidUpiId(settings?.upi_id) ? (() => {
          // SECURITY: build via URLSearchParams + strict UPI VPA validation —
          // an attacker-set upi_id like "victim@upi&am=99999&x=" cannot
          // override the amount or sneak in extra params.
          const upiUrl = buildUpiUrl({
            upi_id: settings.upi_id,
            payee_name: settings?.upi_name || "Owner",
            amount,
            note: `Rent - ${me?.name || "Tenant"}`,
          });
          // buildUpiUrl returns null when the amount is 0 / missing /
          // non-finite. Without this guard, <QRCode value={null} /> crashed
          // the whole page with "Cannot read properties of null (reading
          // 'length')". Show a friendly prompt instead.
          if (!upiUrl) {
            return (
              <div style={{ color: h.sc, fontSize: 12, padding: 10, textAlign: "center" }}>
                {t.enterAmountForQr || "Enter a valid amount above to see the UPI QR code."}
              </div>
            );
          }
          return <>
            <div style={{ color: h.ac, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>{!upiOpened ? (t.upiStep1 || "Step 1: Scan QR or tap below to open your UPI app") : (t.upiStep2 || "Step 2: After paying, tap 'I've Paid' to notify landlord")}</div>
            <div style={{ background: "white", padding: 10, borderRadius: 12, margin: "0 auto 10px", width: "fit-content" }}>
              <QRCode value={upiUrl} size={128} />
            </div>
            {!upiOpened ? (
              <a href={upiUrl}
                onClick={(e) => { if (!(Number(amount) > 0)) { e.preventDefault(); flash(t.fillRequired || "Please enter a valid amount", "error"); return; } setUpiOpened(true); }}
                style={{ background: h.ac, color: "#fff", fontSize: 13, fontWeight: 600, textDecoration: "none", display: "block", marginTop: 8, padding: "10px 16px", borderRadius: 10, textAlign: "center" }}>
                {(t.openUpiApp || "Open UPI App")} — ₹{Number(amount).toLocaleString()}
              </a>
            ) : (
              <button onClick={onConfirmUpi} disabled={confirmDisabled}
                style={{ width: "100%", background: confirmDisabled ? "#9ca3af" : (h.gn || "#16a34a"), color: "#fff", fontSize: 13, fontWeight: 600, border: "none", marginTop: 8, padding: "12px 16px", borderRadius: 10, cursor: confirmDisabled ? "not-allowed" : "pointer" }}>
                {"✓ " + (t.iHavePaid || "I've Paid")}
              </button>
            )}
            <div style={{ color: h.sc, fontSize: 11, marginTop: 6 }}>{t.tapUpi || "Scan the QR or tap to open your UPI app"}</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 6 }}>
              <span style={{ color: h.sc, fontSize: 10 }}>UPI ID:</span>
              <span style={{ color: h.tx, fontSize: 11, fontWeight: 500 }}>{settings?.upi_id}</span>
              <CopyBtn value={settings?.upi_id} label="UPI ID" h={h} />
            </div>
          </>;
        })() : <div style={{ color: h.rd, fontSize: 12, padding: 10 }}>{t.upiNotLoaded || "Landlord hasn't configured a UPI ID yet. Please use Cash or Bank."}</div>}
      </div>}

      {payMth === "bank" && <div style={{ background: h.cd, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${h.bd}` }}>
        <div style={{ color: h.tx, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{t.bankTransferDetails || "Bank Transfer Details"}</div>
        {(() => {
          // getBankRows() prefers structured fields, but if the landlord has
          // only saved the legacy single-blob `bank_details`, it client-side
          // parses that blob into the same 4 rows so the tenant always gets
          // per-field copy buttons (Account Name / Number / IFSC / Bank).
          const rows = getBankRows(settings);
          if (rows.length === 0) {
            return <div style={{ color: h.sc, fontSize: 12 }}>{t.notConfigured || "Not configured"}</div>;
          }
          return rows.map(r => (
            <div key={r.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px dashed ${h.bd}` }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: h.sc, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>{r.label}</div>
                <div style={{ color: h.tx, fontSize: 13, fontWeight: 500, wordBreak: "break-all" }}>{r.value}</div>
              </div>
              <CopyBtn value={r.value} label={r.label} h={h} />
            </div>
          ));
        })()}
      </div>}

      {payMth === "cash" && <div style={{ background: h.cd, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${h.bd}` }}>
        <div style={{ color: h.tx, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>💵 {t.cashPayment || "Cash Payment"}</div>
        <div style={{ color: h.sc, fontSize: 12 }}>{t.cashHint || "Hand over the cash to your landlord and tap Submit to notify them."}</div>
      </div>}

      {payMth !== "upi" && <button onClick={onConfirmNonUpi} disabled={confirmDisabled}
        style={{ width: "100%", padding: "14px 20px", borderRadius: 12, background: confirmDisabled ? "#9ca3af" : `linear-gradient(135deg, ${h.ac}, #1d4ed8)`, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: confirmDisabled ? "not-allowed" : "pointer", boxShadow: !confirmDisabled ? `0 4px 12px ${h.ac}40` : "none", transition: "all 0.2s" }}>
        {submitting ? (t.submitting || "Submitting...") : `${t.submitPay || "Submit Payment"} — ₹${Number(amount).toLocaleString()}`}
      </button>}

      <div style={{ color: h.sc, fontSize: 10, marginTop: 8, textAlign: "center" }}>💡 {t.payVerifyMsg || "Payment will reflect only after landlord verifies and approves"}</div>
    </>
  );

  // ── Advance-only mode (no due payment) ────────────────────────────
  if (!dp) {
    const advAmt = Number(payAmt);
    const advValid = advAmt >= 100;
    const lastPaid = pays.filter(x => x.tid === ct && x.st === "paid").sort((a, b) => (b.mo || "").localeCompare(a.mo || ""))[0];

    const submitAdvance = async () => {
      if (submitting || !advValid || payLockRef.current) return;
      if (payMth === "upi" && !settings?.upi_id) { flash(t.upiNotLoaded || "Landlord hasn't configured UPI", "error"); return; }
      payLockRef.current = true;
      setSubmitting(true);
      try {
        const now = new Date();
        const nextMo = `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][now.getMonth()]} ${String(now.getFullYear()).slice(-2)}`;
        const advPayId = `pay_adv_${Date.now()}`;
        const dtStr = now.toISOString().slice(0, 10);
        const newPay = {
          id: advPayId, tid: ct, amt: 0, com: 0, mo: nextMo, mth: payMth,
          st: "due", dt: dtStr,
          pending: { amt: advAmt, mth: payMth, dt: dtStr, status: "waiting" },
          txns: [{ id: `tx${Date.now()}`, tid: ct, amt: advAmt, mth: payMth, dt: dtStr, type: "advance", status: "pending", mo: nextMo, totalWas: 0 }]
        };
        setPays([...pays, newPay]);
        await api.savePayment(newPay);
        addNotif("landlord", null, `💰 ${me?.name} submitted advance ₹${advAmt.toLocaleString()} (${payMth.toUpperCase()}) — awaiting approval`, "payment");
        flash(t.paySubmitted || `Advance ₹${advAmt.toLocaleString()} submitted. Awaiting landlord approval.`);
        go("dash");
      } catch (err) {
        flash("Error: " + (err.message || "Could not save payment"));
        setSubmitting(false);
        payLockRef.current = false;
      }
    };

    return <div style={{ paddingTop: 16 }}>
      <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
      <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>{t.payAdvance || "Pay Advance"}</h2>
      <div style={{ ...crd(h), padding: 20 }}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 50, height: 50, borderRadius: 25, background: `${h.ac}15`, marginBottom: 8 }}><span style={{ fontSize: 24 }}>💰</span></div>
          <div style={{ color: "#16a34a", fontWeight: 600, fontSize: 14 }}>✓ {t.allCleared || "All dues cleared!"}</div>
          <div style={{ color: h.sc, fontSize: 12, marginTop: 4 }}>{t.payAdvanceHint || "Pay advance for upcoming months"}</div>
          {lastPaid && <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>{t.lastPaid || "Last paid"}: {lastPaid.mo} — ₹{(lastPaid.amt + (lastPaid.com || 0)).toLocaleString()}</div>}
        </div>

        <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>{t.enterAdvanceAmt || "Enter Advance Amount"}</label>
        <div style={{ position: "relative", marginBottom: 12 }}>
          <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: h.tx, fontSize: 18, fontWeight: 700 }}>₹</span>
          <input type="number" value={payAmt} onChange={e => setPayAmt(Math.max(0, Number(e.target.value)))} style={{ ...inpS(h), width: "100%", paddingLeft: 36, fontSize: 20, fontWeight: 700, border: `1.5px solid ${h.ac}`, background: `${h.ac}08` }} />
        </div>
        {advAmt >= 100 && <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, padding: "8px 12px", background: `${h.ac}08`, borderRadius: 10, border: `1px solid ${h.ac}30` }}>
          <span style={{ fontSize: 14 }}>💰</span>
          <span style={{ color: h.ac, fontSize: 12, fontWeight: 600 }}>{t.advanceCreditMsg || `Advance ₹${advAmt.toLocaleString()} will be credited to your account`}</span>
        </div>}
        {advAmt > 0 && advAmt < 100 && <div style={{ color: "#dc2626", fontSize: 11, marginBottom: 8 }}>{t.minAdvance || "Minimum advance: ₹100"}</div>}

        {renderMethodPanel({
          amount: advAmt,
          onConfirmUpi: submitAdvance,
          onConfirmNonUpi: submitAdvance,
          confirmDisabled: submitting || !advValid
        })}
      </div>
    </div>;
  }

  // ── Normal payment mode (due payment exists) ──────────────────────
  const isPartial = payType === "partial";
  const isAdvance = payType === "advance";
  const isValid = payType === "full" ? true
    : payType === "partial" ? (payAmt >= 1 && payAmt < tot)
    : payType === "advance" ? (payAmt > tot)
    : payAmt >= 1;

  const handleTypeChange = (type) => {
    setPayType(type);
    setUpiOpened(false);
    if (type === "full") setPayAmt(tot);
    else if (type === "partial") setPayAmt(Math.round(tot / 2));
    else if (type === "advance") setPayAmt(tot + 1000);
  };

  // ── Manual Payment Handler (records pending, awaits landlord approval) ─
  // Allocates the submitted amount across all underlying rows:
  //   • Smallest maintenance first (so a small partial knocks out small bills)
  //   • Rent row last
  //   • For "advance", any leftover after covering everything goes onto the
  //     rent row's pending amount as advance credit.
  const submitPayment = async () => {
    if (submitting || !isValid || payLockRef.current) return;
    if (payMth === "upi" && !settings?.upi_id) { flash(t.upiNotLoaded || "Landlord hasn't configured UPI", "error"); return; }
    payLockRef.current = true;
    setSubmitting(true);
    try {
      const amt = Number(payAmt);
      const now = new Date();
      const dtStr = now.toISOString().slice(0, 10);
      // Allocation order: smallest maint first, then rent. Skip rows that
      // already have a pending submission (don't double-submit).
      const orderedRows = [
        ...maintRows.filter(r => !r.pending).sort((a, b) => ((a.amt || 0) + (a.com || 0)) - ((b.amt || 0) + (b.com || 0))),
        ...(rentRow && !rentRow.pending ? [rentRow] : [])
      ];
      let remaining = amt;
      const updates = [];
      const baseTs = Date.now();
      // batchId links every row's pending+txn from this single submission so
      // the landlord PayPage can group them under one "₹{total} batch" banner
      // and offer an "Approve all" affordance.
      const batchId = `bp_${baseTs}`;
      for (let i = 0; i < orderedRows.length; i++) {
        if (remaining <= 0) break;
        const row = orderedRows[i];
        const rowTotal = (row.amt || 0) + (row.com || 0);
        const allocated = Math.min(remaining, rowTotal);
        remaining -= allocated;
        const txn = {
          id: `tx${baseTs}_${i}`, tid: ct, amt: allocated, mth: payMth, dt: dtStr,
          type: payType, status: "pending", mo: row.mo, totalWas: rowTotal,
          batchId, batchTotal: amt
        };
        updates.push({
          ...row,
          pending: { amt: allocated, mth: payMth, dt: dtStr, status: "waiting", batchId, batchTotal: amt },
          txns: [...(row.txns || []), txn]
        });
      }
      // Advance: any leftover after covering all dues becomes extra credit
      // on the rent row's pending submission.
      if (payType === "advance" && remaining > 0 && rentRow) {
        const rentUpdate = updates.find(u => u.id === rentRow.id);
        if (rentUpdate) {
          rentUpdate.pending.amt += remaining;
          rentUpdate.txns[rentUpdate.txns.length - 1].amt += remaining;
        }
      }
      // Persist + update local state
      const updateById = new Map(updates.map(u => [u.id, u]));
      setPays(pays.map(x => updateById.get(x.id) || x));
      await Promise.all(updates.map(u => api.savePayment(u)));
      const label = payType === "full" ? `full ₹${amt.toLocaleString()}`
        : payType === "advance" ? `advance ₹${amt.toLocaleString()} (₹${Math.max(0, amt - tot).toLocaleString()} extra)`
        : `partial ₹${amt.toLocaleString()} (₹${Math.max(0, grossTotal - alreadyPaid - amt).toLocaleString()} remaining)`;
      addNotif("landlord", null, `💳 ${me?.name} submitted ${label} (${payMth.toUpperCase()}) — awaiting approval`, "payment");
      flash(t.paySubmitted || `Payment ₹${amt.toLocaleString()} submitted. Awaiting landlord approval.`);
      go("dash");
    } catch (err) {
      flash("Error: " + (err.message || "Could not save payment"));
      setSubmitting(false);
      payLockRef.current = false;
    }
  };

  return <div style={{ paddingTop: 16 }}>
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
    <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>{t.payNow}</h2>

    <div style={{ ...crd(h), padding: 20 }}>
      {/* ── Total Due ──────────────────────────────────── */}
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <div style={{ color: h.sc, fontSize: 12 }}>{alreadyPaid > 0 ? (t.remainingDue || "Remaining Due") : (t.totalDue || "Total Due")}</div>
        <div style={{ color: tot > 0 ? h.rd : "#16a34a", fontWeight: 700, fontSize: 28 }}>₹{tot.toLocaleString()}</div>
        <div style={{ color: h.sc, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 4 }}>
          <span>{t.rentAmt} ₹{rentAmt.toLocaleString()}</span> +
          <span onClick={() => setShowCommonSplit(true)} style={{ color: h.ac, fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}>{maintRows.length > 0 ? (t.commonAndMaint || "Common & Maint") : t.commonAmt} ₹{commonAndMaint.toLocaleString()}</span>
        </div>
        {alreadyPaid > 0 && <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 6, padding: "4px 12px", background: "rgba(22,163,74,0.08)", borderRadius: 8 }}>
          <span style={{ color: "#16a34a", fontSize: 12, fontWeight: 600 }}>✅ {t.alreadyPaid || "Already paid"}: ₹{alreadyPaid.toLocaleString()}</span>
          <span style={{ color: h.sc, fontSize: 11 }}>{t.of || "of"} ₹{grossTotal.toLocaleString()}</span>
        </div>}
      </div>

      {/* ── Common Charges Modal ───────────────────────── */}
      {showCommonSplit && (() => {
        const cc = myCC;
        const hasItems = cc && cc.items && cc.items.length > 0;
        const ccTotal = cc ? (hasItems ? cc.items.reduce((s, it) => s + Number(it.amount || 0), 0) : (cc.eb + cc.cl)) : 0;
        return <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ background: h.bg, borderRadius: 20, width: "100%", maxWidth: 360, padding: 24, boxShadow: "0 10px 40px rgba(0,0,0,0.2)", position: "relative" }}>
          <button onClick={() => setShowCommonSplit(false)} style={{ position: "absolute", top: 16, right: 16, background: `${h.bd}50`, border: "none", width: 30, height: 30, borderRadius: 15, color: h.tx, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ width: 50, height: 50, borderRadius: 25, background: `${h.ac}15`, color: h.ac, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}><Ic n="zap" s={24} c={h.ac} /></div>
            <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0 }}>{t.common}</h2>
            <div style={{ color: h.sc, fontSize: 12, marginTop: 4 }}>{cc ? (t.breakdownFor + " " + cc.mo) : t.noChargesYet}</div>
          </div>
          {cc ? <div style={{ background: `${h.ac}05`, borderRadius: 12, padding: 16, border: `1px solid ${h.bd}` }}>
            {hasItems ? cc.items.map((it, idx) => (
              <div key={idx} style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, paddingBottom: idx === cc.items.length - 1 ? 12 : 0, borderBottom: idx === cc.items.length - 1 ? `1px dashed ${h.bd}` : "none" }}><span style={{ color: h.sc, fontSize: 13 }}>{it.name}</span><span style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>₹{Number(it.amount || 0).toLocaleString()}</span></div>
            )) : <>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}><span style={{ color: h.sc, fontSize: 13 }}>{t.electricity}</span><span style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>₹{cc.eb.toLocaleString()}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, paddingBottom: 12, borderBottom: `1px dashed ${h.bd}` }}><span style={{ color: h.sc, fontSize: 13 }}>{t.cleaningTotal}</span><span style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>₹{cc.cl.toLocaleString()}</span></div>
            </>}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}><span style={{ color: h.sc, fontSize: 12, fontWeight: 500 }}>{t.totalBldgBill}</span><span style={{ color: h.tx, fontWeight: 700, fontSize: 12 }}>₹{ccTotal.toLocaleString()}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: h.ac, fontSize: 12, fontWeight: 600 }}>{t.dividedBy + " " + cc.n + " " + t.active}</span><span style={{ color: h.ac, fontWeight: 700, fontSize: 16 }}>₹{myShare.toLocaleString()}</span></div>
            {myMaintCharges.length > 0 && <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${h.bd}` }}>
              <div style={{ color: h.wn, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>🛠️ {t.perTenantCharges}</div>
              {myMaintCharges.map((mc, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: h.sc, fontSize: 12 }}>{mc.desc}</span><span style={{ color: h.wn, fontWeight: 600, fontSize: 12 }}>₹{mc.exp_amt.toLocaleString()}</span></div>
              ))}
            </div>}
          </div> : <div style={{ background: `${h.ac}05`, borderRadius: 12, padding: 20, border: `1px solid ${h.bd}`, textAlign: "center" }}>
            <div style={{ color: h.sc, fontSize: 13, marginBottom: 8 }}>{t.noChargesMsg}</div>
            <div style={{ color: h.sc, fontSize: 11 }}>{t.chargesWillUpdate}</div>
          </div>}
          {!cc && myMaintCharges.length > 0 && <div style={{ background: `${h.wn}08`, borderRadius: 12, padding: 16, border: `1px solid ${h.wn}30`, marginTop: 12 }}>
            <div style={{ color: h.wn, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>🛠️ {t.perTenantCharges}</div>
            {myMaintCharges.map((mc, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: h.sc, fontSize: 12 }}>{mc.desc}</span><span style={{ color: h.wn, fontWeight: 600, fontSize: 12 }}>₹{mc.exp_amt.toLocaleString()}</span></div>
            ))}
          </div>}
        </div></div>;
      })()}

      {/* ── Payment Type Selector ────────────────────────── */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 8 }}>{t.paymentType || "Payment Type"}</label>
        <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          {[
            { key: "full", icon: "✅", label: t.fullPay || "Full Payment", desc: `₹${tot.toLocaleString()}`, color: "#16a34a" },
            { key: "partial", icon: "📝", label: t.partial || "Partial", desc: t.payLess || "Pay less", color: "#f59e0b" },
            { key: "advance", icon: "💰", label: t.advance || "Advance", desc: t.payExtra || "Pay extra", color: h.ac }
          ].map(opt => (
            <button key={opt.key} onClick={() => handleTypeChange(opt.key)} style={{
              flex: 1, padding: "10px 6px", borderRadius: 12, cursor: "pointer",
              border: `2px solid ${payType === opt.key ? opt.color : h.bd}`,
              background: payType === opt.key ? `${opt.color}10` : "transparent",
              textAlign: "center", transition: "all 0.2s"
            }}>
              <div style={{ fontSize: 18, marginBottom: 2 }}>{opt.icon}</div>
              <div style={{ color: payType === opt.key ? opt.color : h.tx, fontSize: 11, fontWeight: 700 }}>{opt.label}</div>
              <div style={{ color: h.sc, fontSize: 9, marginTop: 1 }}>{opt.desc}</div>
            </button>
          ))}
        </div>

        {/* Amount Input */}
        <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>
          {payType === "full" ? (t.amt || "Amount") : payType === "partial" ? (t.enterPartialAmt || "Enter Partial Amount") : (t.enterAdvanceAmt || "Enter Advance Amount")}
        </label>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: h.tx, fontSize: 18, fontWeight: 700 }}>₹</span>
          <input
            type="number"
            value={payAmt}
            readOnly={payType === "full"}
            onChange={e => {
              const val = Math.max(0, Number(e.target.value));
              setPayAmt(val);
              setUpiOpened(false);
            }}
            style={{
              ...inpS(h), width: "100%", paddingLeft: 36,
              fontSize: 20, fontWeight: 700, textAlign: "left",
              border: `1.5px solid ${isPartial ? "#f59e0b" : isAdvance ? h.ac : "#16a34a"}`,
              background: payType === "full" ? "rgba(22,163,74,0.04)" : isPartial ? "rgba(245,158,11,0.04)" : `${h.ac}08`,
              opacity: payType === "full" ? 0.8 : 1
            }}
          />
        </div>

        {/* Info strip based on type */}
        {isPartial && payAmt > 0 && payAmt < tot && <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "8px 12px", background: "rgba(245,158,11,0.08)", borderRadius: 10, border: "1px solid rgba(245,158,11,0.2)" }}>
          <span style={{ fontSize: 14 }}>⚠️</span>
          <div><div style={{ color: "#d97706", fontSize: 12, fontWeight: 600 }}>{t.partialPayment || "Partial Payment"}</div>
          <div style={{ color: "#b45309", fontSize: 11 }}>{t.remainingDue || "Remaining due"}: ₹{(tot - payAmt).toLocaleString()}</div></div>
        </div>}
        {isPartial && payAmt >= tot && <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "8px 12px", background: "rgba(239,68,68,0.08)", borderRadius: 10, border: "1px solid rgba(239,68,68,0.2)" }}>
          <span style={{ fontSize: 14 }}>❌</span>
          <span style={{ color: "#dc2626", fontSize: 11, fontWeight: 500 }}>{t.partialLessThan || "Partial amount must be less than"} ₹{tot.toLocaleString()}</span>
        </div>}
        {isAdvance && payAmt > tot && <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "8px 12px", background: `${h.ac}08`, borderRadius: 10, border: `1px solid ${h.ac}30` }}>
          <span style={{ fontSize: 14 }}>💰</span>
          <div><div style={{ color: h.ac, fontSize: 12, fontWeight: 600 }}>{t.advancePayment || "Advance Payment"}</div>
          <div style={{ color: h.sc, fontSize: 11 }}>₹{tot.toLocaleString()} {t.duePlus || "due +"} ₹{(payAmt - tot).toLocaleString()} {t.advance || "advance"}</div></div>
        </div>}
        {isAdvance && payAmt <= tot && <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "8px 12px", background: "rgba(239,68,68,0.08)", borderRadius: 10, border: "1px solid rgba(239,68,68,0.2)" }}>
          <span style={{ fontSize: 14 }}>❌</span>
          <span style={{ color: "#dc2626", fontSize: 11, fontWeight: 500 }}>{t.advanceMoreThan || "Advance must be more than"} ₹{tot.toLocaleString()}</span>
        </div>}
      </div>

      {/* ── Method Picker (UPI QR / Cash / Bank) ────────── */}
      {renderMethodPanel({
        amount: Number(payAmt) || 0,
        onConfirmUpi: submitPayment,
        onConfirmNonUpi: submitPayment,
        confirmDisabled: submitting || !isValid
      })}
    </div>
  </div>;
}

export default TenPayNow;
